import { finishCtaPopupSuccess, isCtaPopupSuccessState, resolveCtaPopupContext } from "./form-cta-success";

const AJAX_FORM_SELECTOR = "form.default-form:not([data-ajax-initialized])";

const waitForPaint = () =>
  new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

const isExcludedForm = (form) => {
  if (form.dataset.noAjax !== undefined) {
    return true;
  }
  if (form.closest(".check-typo3-teaser__form")) {
    return true;
  }
  return false;
};

const getConfiguredThankYouUrl = (form) => {
  return form.querySelector("#thank_you_page")?.textContent?.trim() || "";
};

const resolveThankYouUrl = (form, locationHeader) => {
  if (locationHeader) {
    try {
      return new URL(locationHeader, window.location.href).href;
    } catch {
      return locationHeader;
    }
  }

  return getConfiguredThankYouUrl(form);
};

const shouldNavigateToThankYou = (url) => {
  if (!url) {
    return false;
  }

  try {
    const target = new URL(url, window.location.href);
    const current = new URL(window.location.href);

    return target.pathname !== current.pathname
      || target.search !== current.search
      || target.hash !== current.hash;
  } catch {
    return true;
  }
};

const isSpamEmail = (form) => {
  const spamEl = form.querySelector("#spam_email_text");
  const spamText = spamEl?.textContent?.trim();
  if (!spamText) {
    return false;
  }

  const blockedDomains = spamText
    .split(",")
    .map((domain) => domain.trim().toLowerCase())
    .filter(Boolean);

  if (!blockedDomains.length) {
    return false;
  }

  const emailInputs = form.querySelectorAll('input[type="email"], .form-control[type="email"]');

  for (const input of emailInputs) {
    const value = input.value.trim().toLowerCase();
    if (!value) {
      continue;
    }

    const atIndex = value.lastIndexOf("@");
    if (atIndex === -1) {
      continue;
    }

    const domain = value.slice(atIndex + 1);
    if (blockedDomains.some((blocked) => domain === blocked || domain.endsWith(`.${blocked}`) || domain.startsWith(`${blocked}.`))) {
      input.classList.add("error");
      input.closest(".form-group")?.classList.add("form-group-error");

      const customError = input.closest(".form-group")?.querySelector(".custom-email-error");
      if (customError) {
        customError.classList.remove("d-none");
      }

      return true;
    }
  }

  return false;
};

const hasServerValidationErrors = (element) => {
  if (!element || element.tagName !== "FORM") {
    return false;
  }

  return Boolean(
    element.querySelector(".form-group.has-error, .error.help-block, .alert-danger")
  );
};

const isMultiStepAdvance = (parsedForm) => {
  if (!parsedForm || parsedForm.tagName !== "FORM") {
    return false;
  }

  return Boolean(
    parsedForm.querySelector(".form-navigation")
      && !parsedForm.querySelector(".btn--form-submit")
      && parsedForm.querySelector(".btn-group.next button")
  );
};

const isConfirmationElement = (element, formId) => {
  return Boolean(element && element.tagName === "DIV" && element.id === formId);
};

const getCtaPopupContext = (form) => resolveCtaPopupContext(form);

const isSuccessfulFormPostResponse = (response) => {
  if (response.ok) {
    return true;
  }

  if (response.status >= 300 && response.status < 400) {
    return true;
  }

  return response.type === "opaqueredirect" || response.status === 0;
};

const isRedirectResponse = (response) => {
  return response.status >= 300 && response.status < 400;
};

const fetchHtmlAfterFormPost = async (response, actionUrl) => {
  if (response.ok) {
    return response.text();
  }

  if (isRedirectResponse(response)) {
    const location = response.headers.get("Location");
    const followUrl = location
      ? new URL(location, window.location.href).href
      : actionUrl;
    const followResponse = await fetch(followUrl, {
      credentials: "same-origin",
      headers: {
        "X-Requested-With": "XMLHttpRequest",
      },
    });

    if (followResponse.ok) {
      return followResponse.text();
    }
  }

  return null;
};

const setSubmitting = (form, isSubmitting) => {
  if (!form) {
    return;
  }

  const submitButtons = form.querySelectorAll(".btn--form-submit, .form-navigation .btn");
  submitButtons.forEach((button) => {
    button.classList.toggle("btn--form-submiting", isSubmitting);
    button.disabled = isSubmitting;
  });

  const ctaContext = resolveCtaPopupContext(form);
  if (!ctaContext || isCtaPopupSuccessState(form)) {
    return;
  }

  const { popup } = ctaContext;
  const contactForm = popup.querySelector(".contact-form");
  const loader = popup.querySelector(".cta-form-loader");

  if (isSubmitting) {
    contactForm?.classList.add("d-none");
    loader?.classList.remove("d-none");
    popup.querySelector(".cta-form-success:not(.cta-form-success--shown)")?.classList.add("d-none");
  } else {
    loader?.classList.add("d-none");
    contactForm?.classList.remove("d-none");
  }
};

const finishSubmitLoading = (form) => {
  document.dispatchEvent(new CustomEvent("form:submit-end"));
  if (form) {
    setSubmitting(form, false);
  }
};

const endSubmitLoading = (form) => {
  finishSubmitLoading(form);
};

const startSubmitLoading = async (form) => {
  setSubmitting(form, true);
  document.dispatchEvent(new CustomEvent("form:submit-start"));
  await waitForPaint();
};

let pendingNavigationForm = null;

const registerNavigationCleanup = (form) => {
  pendingNavigationForm = form;

  const cleanup = () => {
    document.removeEventListener("barba:after-enter", onEnter);
    document.removeEventListener("form:navigate-error", onError);
    clearTimeout(timeoutId);
    pendingNavigationForm = null;
  };

  const onEnter = () => {
    finishSubmitLoading(pendingNavigationForm);
    cleanup();
  };

  const onError = () => {
    if (pendingNavigationForm && document.contains(pendingNavigationForm)) {
      finishSubmitLoading(pendingNavigationForm);
      showFormError(pendingNavigationForm, "Something went wrong. Please try again.");
    }
    cleanup();
  };

  const timeoutId = setTimeout(onError, 15000);

  document.addEventListener("barba:after-enter", onEnter, { once: true });
  document.addEventListener("form:navigate-error", onError, { once: true });
};

const showFormError = (form, message) => {
  const navigation = form.querySelector(".form-navigation");
  if (!navigation) {
    return;
  }

  let errorEl = form.querySelector(".form-ajax-error");
  if (!errorEl) {
    errorEl = document.createElement("p");
    errorEl.className = "form-ajax-error text-danger mt-3 mb-0";
    navigation.appendChild(errorEl);
  }

  errorEl.textContent = message;
};

const clearFormError = (form) => {
  form.querySelector(".form-ajax-error")?.remove();
};

const resetCaptchas = (form) => {
  form.querySelectorAll(".frc-captcha").forEach((widget) => {
    if (typeof widget.reset === "function") {
      try {
        widget.reset();
      } catch {
        // Friendly Captcha widget API may vary
      }
    }
  });

  if (typeof window.grecaptcha !== "undefined") {
    form.querySelectorAll(".g-recaptcha").forEach((element) => {
      const widgetId = element.getAttribute("data-widget-id");
      if (widgetId) {
        try {
          window.grecaptcha.reset(parseInt(widgetId, 10));
        } catch {
          // ignore reset failures
        }
      }
    });
  }
};

const reinitFormModules = async () => {
  const [{ initForms, initFormSelects }, { initCustomImageUpload }] = await Promise.all([
    import("./form"),
    import("./custom-image-upload"),
  ]);

  initForms();
  initFormSelects();
  initCustomImageUpload();
  initAjaxFormSubmission();
};

const parseResponseElement = (html, formId) => {
  if (!html) {
    return null;
  }

  const doc = new DOMParser().parseFromString(html, "text/html");
  if (formId) {
    return doc.getElementById(formId) || doc.querySelector(`form#${formId}`);
  }

  return doc.querySelector("form.default-form");
};

const replaceFormFromHtml = (currentForm, html) => {
  if (resolveCtaPopupContext(currentForm)) {
    return null;
  }

  const parsed = parseResponseElement(html, currentForm.id);
  if (!parsed) {
    return null;
  }

  const imported = document.importNode(parsed, true);
  currentForm.replaceWith(imported);

  return imported.tagName === "FORM" ? imported : null;
};

const showInlineConfirmation = (form, html) => {
  const parsed = parseResponseElement(html, form.id);
  if (!isConfirmationElement(parsed, form.id)) {
    return false;
  }

  const imported = document.importNode(parsed, true);
  imported.classList.add("form-confirmation-message");
  form.replaceWith(imported);
  finishSubmitLoading(null);
  return true;
};

const navigateToThankYou = (form, url) => {
  if (!url) {
    return false;
  }

  finishSubmitLoading(form);

  try {
    window.location.assign(new URL(url, window.location.href).href);
  } catch {
    window.location.assign(url);
  }

  return true;
};

const getRedirectUrlFromResponse = (form, response) => {
  const location = response.headers?.get("Location") || null;
  return resolveThankYouUrl(form, location);
};

const getSubmissionOutcome = (form, response, html) => {
  const location = response.headers?.get("Location") || null;
  const redirectUrl = resolveThankYouUrl(form, location);
  const configuredThankYou = getConfiguredThankYouUrl(form);
  const parsed = html ? parseResponseElement(html, form.id) : null;

  if (hasServerValidationErrors(parsed)) {
    return { type: "validation", html };
  }

  if (isMultiStepAdvance(parsed)) {
    return { type: "multistep", html };
  }

  if (location && redirectUrl && shouldNavigateToThankYou(redirectUrl)) {
    return { type: "redirect", url: redirectUrl };
  }

  if (html && isConfirmationElement(parsed, form.id)) {
    return { type: "confirmation", html };
  }

  if (parsed && parsed.tagName === "FORM") {
    return { type: "form-update", html };
  }

  if (configuredThankYou && shouldNavigateToThankYou(configuredThankYou)) {
    return { type: "redirect", url: configuredThankYou };
  }

  if (redirectUrl) {
    return { type: "redirect", url: redirectUrl };
  }

  return { type: "error" };
};

const applySubmissionOutcome = async (form, outcome) => {
  clearFormError(form);

  switch (outcome.type) {
    case "validation": {
      const updatedForm = outcome.html ? replaceFormFromHtml(form, outcome.html) || form : form;
      await reinitFormModules();
      endSubmitLoading(updatedForm);
      resetCaptchas(updatedForm);
      return;
    }
    case "multistep": {
      const updatedForm = outcome.html ? replaceFormFromHtml(form, outcome.html) : null;
      await reinitFormModules();
      endSubmitLoading(updatedForm || form);
      return;
    }
    case "confirmation": {
      if (showInlineConfirmation(form, outcome.html)) {
        return;
      }
      break;
    }
    case "redirect": {
      if (navigateToThankYou(form, outcome.url)) {
        return;
      }
      break;
    }
    case "form-update": {
      const updatedForm = replaceFormFromHtml(form, outcome.html);
      if (updatedForm) {
        await reinitFormModules();
        endSubmitLoading(updatedForm);
        return;
      }
      break;
    }
    default:
      break;
  }

  endSubmitLoading(form);
  showFormError(form, "Something went wrong. Please try again.");
};

const handleCtaPopupResult = async (form, html) => {
  const ctaContext = getCtaPopupContext(form);
  if (!ctaContext) {
    return false;
  }

  const { popup, thanksEl } = ctaContext;
  const parsedForm = html ? parseResponseElement(html, form.id) : null;
  const successOptions = { responseHtml: html || "", formId: form.id };

  if (hasServerValidationErrors(parsedForm)) {
    const updatedForm = html ? replaceFormFromHtml(form, html) || form : form;
    await reinitFormModules();
    endSubmitLoading(updatedForm);
    resetCaptchas(updatedForm);
    return true;
  }

  if (isMultiStepAdvance(parsedForm)) {
    const updatedForm = html ? replaceFormFromHtml(form, html) : null;
    await reinitFormModules();
    endSubmitLoading(updatedForm || form);
    return true;
  }

  clearFormError(form);
  finishCtaPopupSuccess(popup, form, { ...successOptions, thanksEl });
  return true;
};

const createSubmissionBody = (form, submitter) => {
  let body;
  try {
    body = submitter ? new FormData(form, submitter) : new FormData(form);
  } catch {
    body = new FormData(form);
  }

  if (!submitter?.name?.includes("[__currentPage]")) {
    return body;
  }

  for (const key of body.keys()) {
    if (key.includes("[__currentPage]")) {
      return body;
    }
  }

  body.append(submitter.name, submitter.value || "1");
  return body;
};

const submitFormAjax = async (form, submitter = null) => {
  clearFormError(form);
  await startSubmitLoading(form);

  const actionUrl = form.getAttribute("action") || window.location.href;

  try {
    const response = await fetch(actionUrl, {
      method: form.getAttribute("method") || "POST",
      body: createSubmissionBody(form, submitter),
      credentials: "same-origin",
      redirect: "manual",
      headers: {
        "X-Requested-With": "XMLHttpRequest",
      },
    });

    if (!isSuccessfulFormPostResponse(response)) {
      endSubmitLoading(form);
      showFormError(form, "Something went wrong. Please try again.");
      return;
    }

    if (getCtaPopupContext(form)) {
      const html = await fetchHtmlAfterFormPost(response, actionUrl);
      await handleCtaPopupResult(form, html);
      return;
    }

    const redirectUrl = getRedirectUrlFromResponse(form, response);
    if (isRedirectResponse(response) && redirectUrl && shouldNavigateToThankYou(redirectUrl)) {
      clearFormError(form);
      navigateToThankYou(form, redirectUrl);
      return;
    }

    const html = await fetchHtmlAfterFormPost(response, actionUrl);
    const outcome = getSubmissionOutcome(form, response, html);
    await applySubmissionOutcome(form, outcome);
  } catch {
    endSubmitLoading(form);
    showFormError(form, "Network error. Please check your connection and try again.");
  }
};

const handleAjaxSubmit = (event) => {
  const form = event.currentTarget;

  if (!form.checkValidity()) {
    return;
  }

  if (isSpamEmail(form)) {
    event.preventDefault();
    event.stopPropagation();
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  submitFormAjax(form, event.submitter || null);
};

export const initAjaxFormSubmission = () => {
  document.querySelectorAll(AJAX_FORM_SELECTOR).forEach((form) => {
    if (isExcludedForm(form)) {
      return;
    }

    form.dataset.ajaxInitialized = "1";
    form.addEventListener("submit", handleAjaxSubmit, false);
  });
};
